/**
 * Structural loading placeholders.
 *
 * WHY NOT A SPINNER. A spinner says "something is happening" and nothing else:
 * the page stays empty, then everything appears at once and the layout jumps.
 * These mirror the shape of the content that is coming, so the page has its
 * final geometry before the data lands and the eye already knows where to look.
 *
 * COMPOSE, DO NOT GENERALISE. Each piece matches ONE real layout primitive this
 * app actually uses -- a page header, a KPI row, a filter bar, a table. A page's
 * skeleton is those pieces assembled in the same order as the page itself, so a
 * wrong skeleton is visible as a wrong shape rather than hiding behind a generic
 * grey box.
 *
 * ACCESSIBILITY. The outermost element of a loading region carries
 * role="status" and an aria-label, so a screen reader announces the wait once
 * rather than reading dozens of empty placeholder nodes.
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Skeleton } from './Skeleton';
import styles from './PageSkeleton.module.css';

/**
 * Whether a piece is already inside a page-level placeholder.
 *
 * Each piece announces itself when used alone -- a table loading inside a
 * finished page is its own wait. Composed into a page, they must NOT: a
 * screen reader would hear "Loading summary figures", "Loading results",
 * "Loading page" for one event. The page owns the announcement; the pieces
 * inside it go quiet. This file's own docstring has always said "announces
 * the wait once"; this is what makes that true.
 */
const InsidePageSkeleton = createContext(false);

/** role + label for a piece, unless a page placeholder is already announcing. */
function useStatus(label: string): { role?: 'status'; 'aria-label'?: string } {
  return useContext(InsidePageSkeleton) ? {} : { role: 'status', 'aria-label': label };
}

/** Deterministic pseudo-random width, so rows look organic but never reflow. */
function widthFor(seed: number, min = 45, max = 95): string {
  const span = max - min;
  // A cheap hash: stable per seed, no Math.random, so re-renders are identical.
  return `${min + ((seed * 37) % span)}%`;
}

export interface SkeletonTextProps {
  readonly lines?: number;
  readonly className?: string;
}

/** A paragraph of text. The last line is short, as real text usually is. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={clsx(styles.stack, className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height={12} width={i === lines - 1 ? '55%' : widthFor(i, 75, 100)} />
      ))}
    </div>
  );
}

export interface SkeletonPageHeaderProps {
  /** How many action buttons sit on the right of the title. */
  readonly actions?: number;
  readonly withSubtitle?: boolean;
}

/** Title, optional subtitle, and the action cluster — the PageHeader shape. */
export function SkeletonPageHeader({ actions = 0, withSubtitle = true }: SkeletonPageHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <Skeleton height={28} width={220} radius="8px" />
        {withSubtitle && <Skeleton height={13} width={340} />}
      </div>
      {actions > 0 && (
        <div className={styles.headerActions}>
          {Array.from({ length: actions }, (_, i) => (
            <Skeleton key={i} height={34} width={110} radius="var(--radius-md)" />
          ))}
        </div>
      )}
    </div>
  );
}

/** The row of KPI / stat cards that sits under most page headers. */
export function SkeletonKpiRow({ cards = 4 }: { cards?: number }) {
  return (
    <div className={styles.kpiRow} {...useStatus('Loading summary figures')}>
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className={styles.kpiCard}>
          <Skeleton height={11} width="55%" />
          <Skeleton height={26} width="40%" radius="6px" />
          <Skeleton height={10} width="70%" />
        </div>
      ))}
    </div>
  );
}

/** A horizontal tab strip. */
export function SkeletonTabs({ tabs = 3 }: { tabs?: number }) {
  return (
    <div className={styles.tabs}>
      {Array.from({ length: tabs }, (_, i) => (
        <Skeleton key={i} height={32} width={i === 0 ? 120 : 100} radius="var(--radius-full)" />
      ))}
    </div>
  );
}

export interface SkeletonFilterBarProps {
  /** Dropdowns / selects to the left. */
  readonly filters?: number;
  /** Whether a wide search field sits in the row. */
  readonly withSearch?: boolean;
}

/** The "page size · search · sort" control row above a table. */
export function SkeletonFilterBar({ filters = 2, withSearch = true }: SkeletonFilterBarProps) {
  return (
    <div className={styles.filterBar}>
      {Array.from({ length: filters }, (_, i) => (
        <Skeleton key={i} height={38} width={150} radius="var(--radius-md)" />
      ))}
      {withSearch && <Skeleton height={38} width="100%" radius="var(--radius-md)" />}
    </div>
  );
}

export interface SkeletonTableProps {
  readonly rows?: number;
  readonly columns?: number;
  /** Render a header strip above the rows, matching a real table. */
  readonly withHeader?: boolean;
  /**
   * Accessible name for the loading region. Callers that already have an
   * established name keep it -- DataTable has announced "Loading results"
   * since it shipped, and renaming it would change the contract screen-reader
   * users (and its test) rely on.
   */
  readonly label?: string;
}

/**
 * A table with a header strip and staggered cell widths.
 *
 * The plain TableSkeleton draws every cell full width, which reads as a grey
 * block rather than a table. Real columns differ, so these do too -- and the
 * first column is wider, because in every table here it is the name.
 */
export function SkeletonTable({
  rows = 8,
  columns = 6,
  withHeader = true,
  label = 'Loading table',
}: SkeletonTableProps) {
  return (
    <div className={styles.table} {...useStatus(label)}>
      {withHeader && (
        <div className={styles.tableHeadRow}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} height={10} width={c === 0 ? '60%' : '45%'} />
          ))}
        </div>
      )}
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className={styles.tableRow}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton
              key={c}
              height={13}
              width={c === 0 ? widthFor(r, 60, 95) : widthFor(r + c, 40, 80)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface SkeletonTableRowsProps {
  readonly rows?: number;
  readonly columns: number;
  /**
   * Class for each <td>, so the cells keep the host table's own padding.
   * Explicitly `| undefined` because CSS-module lookups are typed that way and
   * this project runs exactOptionalPropertyTypes.
   */
  readonly cellClassName?: string | undefined;
}

/**
 * Skeleton rows for a REAL <table>, to be rendered inside its <tbody>.
 *
 * The div-based SkeletonTable cannot be used there: a <div> inside <tbody> is
 * invalid HTML and the browser hoists it out of the table, which collapses the
 * column widths and produces exactly the jump these exist to prevent. These
 * emit <tr>/<td> so the host table's own column sizing applies.
 */
export function SkeletonTableRows({ rows = 8, columns, cellClassName }: SkeletonTableRowsProps) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-hidden={r > 0}>
          {Array.from({ length: columns }, (_, c) => (
            <td key={c} className={cellClassName}>
              <Skeleton
                height={13}
                width={c === 0 ? widthFor(r, 60, 95) : widthFor(r + c, 40, 85)}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** A responsive grid of cards — projects, organisations, imports. */
export function SkeletonCardGrid({ cards = 6, lines = 2 }: { cards?: number; lines?: number }) {
  return (
    <div className={styles.cardGrid} {...useStatus('Loading cards')}>
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className={styles.card}>
          <div className={styles.cardTop}>
            <Skeleton height={32} width={32} radius="8px" />
            <div className={styles.stack}>
              <Skeleton height={14} width={widthFor(i, 55, 85)} />
              <Skeleton height={11} width="40%" />
            </div>
          </div>
          <SkeletonText lines={lines} />
          <div className={styles.cardFooter}>
            <Skeleton height={24} width={80} radius="var(--radius-full)" />
            <Skeleton height={24} width={64} radius="var(--radius-full)" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A labelled key/value detail block, as used by drawers and detail pages. */
export function SkeletonDetailList({ rows = 6 }: { rows?: number }) {
  return (
    <div className={styles.detailList} {...useStatus('Loading details')}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={styles.detailRow}>
          <Skeleton height={10} width={90} />
          <Skeleton height={13} width={widthFor(i, 50, 90)} />
        </div>
      ))}
    </div>
  );
}

/** Stacked form fields, for drawers and modals that load before they render. */
export function SkeletonForm({ fields = 4 }: { fields?: number }) {
  return (
    <div className={styles.form} {...useStatus('Loading form')}>
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className={styles.stack}>
          <Skeleton height={11} width={110} />
          <Skeleton height={38} radius="var(--radius-md)" />
        </div>
      ))}
    </div>
  );
}

export interface SkeletonPageProps {
  readonly headerActions?: number;
  readonly kpis?: number;
  readonly tabs?: number;
  readonly filters?: number;
  readonly columns?: number;
  readonly rows?: number;
}

/**
 * The page container, for a screen whose shape is its own.
 *
 * SkeletonListPage covers the common header-KPIs-tabs-filters-table spine. A
 * dashboard of cards, a two-column detail view or a page with a side rail
 * composes the pieces itself and needs this wrapper to sit them in, with the
 * one `role="status"` the whole placeholder announces through.
 */
export function SkeletonPageShell({ children }: { children: ReactNode }) {
  return (
    <InsidePageSkeleton.Provider value={true}>
      <div className={styles.page} role="status" aria-label="Loading page">
        {children}
      </div>
    </InsidePageSkeleton.Provider>
  );
}

/**
 * A side rail of links, as Settings and Master Data use.
 *
 * Grouped, because both rails are: a short heading, then its items.
 */
export function SkeletonSideNav({ groups = 3, itemsPerGroup = 3 }: {
  groups?: number;
  itemsPerGroup?: number;
}) {
  return (
    <div className={styles.sideNav}>
      {Array.from({ length: groups }, (_, g) => (
        <div key={g} className={styles.sideNavGroup}>
          <Skeleton width="42%" height={9} />
          {Array.from({ length: itemsPerGroup }, (_, i) => (
            <Skeleton key={i} width={`${72 - ((g + i) % 3) * 12}%`} height={14} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** A page split into a narrow rail and its content. */
export function SkeletonSplit({ children }: { children: ReactNode }) {
  return <div className={styles.split}>{children}</div>;
}

/**
 * The common "header → KPIs → tabs → filters → table" page, assembled.
 *
 * A convenience for the many list pages that share that exact spine. A page
 * whose shape differs composes the pieces itself rather than bending this.
 */
export function SkeletonListPage({
  headerActions = 2,
  kpis = 4,
  tabs = 3,
  filters = 2,
  columns = 6,
  rows = 8,
}: SkeletonPageProps) {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={headerActions} />
      {kpis > 0 && <SkeletonKpiRow cards={kpis} />}
      {tabs > 0 && <SkeletonTabs tabs={tabs} />}
      {filters > 0 && <SkeletonFilterBar filters={filters} />}
      <SkeletonTable columns={columns} rows={rows} />
    </SkeletonPageShell>
  );
}
